import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import punchHistoryService from '../../../services/punchHistoryService';

const EmployeeModal = ({ 
  employee, 
  isOpen, 
  onClose, 
  onSave, 
  mode = 'view' // 'view', 'edit', 'create'
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    employeeCode: '',
    hireDate: '',
    hireType: 'Vollzeit',
    salary: '',
    gehaltS: 0,
    totalGehaltS: 0,
    tagesrate: '',
    dauerS: 0,
    status: 'active',
    phone: '',
    address: '',
    photo: null
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState('profile');
  
  
  // Punch history state
  const [punchHistory, setPunchHistory] = useState([]);
  const [punchHistoryLoading, setPunchHistoryLoading] = useState(false);
  const [punchHistoryError, setPunchHistoryError] = useState(null);

  useEffect(() => {
    if (employee) {
      setFormData({
        name: employee?.name || '',
        email: employee?.email || '',
        employeeCode: employee?.employeeCode || '',
        hireDate: employee?.hireDate || '',
        hireType: employee?.hireType || 'Vollzeit',
        salary: employee?.salary || '',
        gehaltS: employee?.gehaltS || 0,
        totalGehaltS: employee?.totalGehaltS || 0,
        tagesrate: employee?.tagesrate || '',
        dauerS: employee?.dauerS || 0,
        status: employee?.status || 'active',
        phone: employee?.phone || '',
        address: employee?.address || '',
        photo: employee?.photo || null
      });
    } else if (mode === 'create') {
      setFormData({
        name: '',
        email: '',
        employeeCode: '',
        hireDate: new Date()?.toISOString()?.split('T')?.[0],
        hireType: 'Vollzeit',
        salary: '',
        gehaltS: 0,
        tagesrate: '',
        dauerS: 0,
        status: 'active',
        phone: '',
        address: '',
        photo: null
      });
    }
  }, [employee, mode]);

  // Load punch history when employee changes and we're in view mode
  useEffect(() => {
    if (employee?.id && mode === 'view') {
      loadPunchHistory();
    } else {
      setPunchHistory([]);
      setPunchHistoryError(null);
    }
  }, [employee?.id, mode]);

  // Load punch history data
  const loadPunchHistory = async () => {
    if (!employee?.id) return;
    
    setPunchHistoryLoading(true);
    setPunchHistoryError(null);
    
    try {
      const history = await punchHistoryService.getRecentPunchHistory(employee.id);
      setPunchHistory(history);
    } catch (error) {
      console.error('Error loading punch history:', error);
      setPunchHistoryError('Fehler beim Laden der Stempelhistorie');
      setPunchHistory([]);
    } finally {
      setPunchHistoryLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) newErrors.name = 'Name ist erforderlich';
    if (!formData?.employeeCode?.trim()) newErrors.employeeCode = 'Mitarbeiternummer ist erforderlich';
    if (formData?.salary !== '' && formData?.salary < 0) newErrors.salary = 'Gehalt darf nicht negativ sein';
    if (formData?.tagesrate !== '' && formData?.tagesrate < 0) newErrors.tagesrate = 'Tagesrate darf nicht negativ sein';
    
    // Email validation (only if email is provided)
    if (formData?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoUpload = (event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData(prev => ({
          ...prev,
          photo: e?.target?.result
        }));
      };
      reader?.readAsDataURL(file);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden elevation-3">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon 
              name={mode === 'create' ? 'UserPlus' : mode === 'edit' ? 'Edit' : 'User'} 
              size={24} 
              className="text-primary" 
            />
            <div>
              <h2 className="text-xl font-semibold text-text-primary">
                {mode === 'create' ? 'Neuen Mitarbeiter hinzufügen' : 
                 mode === 'edit' ? 'Mitarbeiter bearbeiten' : 'Mitarbeiterdetails'}
              </h2>
              <p className="text-sm text-text-secondary">
                {mode === 'create' ? 'Neuen Mitarbeiterdatensatz erstellen' :
                 mode === 'edit' ? 'Mitarbeiterinformationen aktualisieren' : 'Mitarbeiterinformationen anzeigen'}
              </p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
            className="touch-feedback"
          />
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <div className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'profile' ?'border-primary text-primary' :'border-transparent text-text-secondary hover:text-text-primary'
              }`}
            >
              Profilinformationen
            </button>
            {mode === 'view' && (
              <button
                onClick={() => setActiveTab('history')}
                className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'history' ?'border-primary text-primary' :'border-transparent text-text-secondary hover:text-text-primary'
                }`}
              >
                Stempelhistorie
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'profile' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Photo Section */}
              <div className="lg:col-span-1">
                <div className="text-center">
                  <div className="w-32 h-32 mx-auto rounded-full overflow-hidden bg-muted mb-4">
                    {formData?.photo ? (
                      <Image
                        src={formData?.photo}
                        alt={formData?.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon name="User" size={48} className="text-text-secondary" />
                      </div>
                    )}
                  </div>
                  
                  {(mode === 'edit' || mode === 'create') && (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className="hidden"
                        id="photo-upload"
                      />
                      <label htmlFor="photo-upload">
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Camera"
                          iconPosition="left"
                          className="touch-feedback cursor-pointer"
                          asChild
                        >
                          <span>Foto hochladen</span>
                        </Button>
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Form Fields */}
              <div className="lg:col-span-2 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Vollständiger Name"
                    type="text"
                    value={formData?.name}
                    onChange={(e) => handleInputChange('name', e?.target?.value)}
                    error={errors?.name}
                    required
                    disabled={mode === 'view'}
                  />
                  
                  <Input
                    label="Mitarbeiternummer"
                    type="text"
                    value={formData?.employeeCode}
                    onChange={(e) => handleInputChange('employeeCode', e?.target?.value)}
                    error={errors?.employeeCode}
                    required
                    disabled={mode === 'view'}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="E-Mail-Adresse"
                    type="email"
                    value={formData?.email}
                    onChange={(e) => handleInputChange('email', e?.target?.value)}
                    error={errors?.email}
                    disabled={mode === 'view'}
                  />
                  
                  <Input
                    label="Telefonnummer"
                    type="tel"
                    value={formData?.phone}
                    onChange={(e) => handleInputChange('phone', e?.target?.value)}
                    disabled={mode === 'view'}
                  />
                </div>


                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Einstellungsdatum"
                    type="date"
                    value={formData?.hireDate}
                    onChange={(e) => handleInputChange('hireDate', e?.target?.value)}
                    error={errors?.hireDate}
                    disabled={mode === 'view'}
                  />
                  
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Anstellungsart
                    </label>
                    <select
                      value={formData?.hireType}
                      onChange={(e) => handleInputChange('hireType', e?.target?.value)}
                      disabled={mode === 'view'}
                      className="w-full px-3 py-2 border border-border rounded-md bg-input text-text-primary focus:ring-2 focus:ring-primary focus:border-primary disabled:opacity-50"
                    >
                      <option value="Vollzeit">Vollzeit</option>
                      <option value="Teilzeit">Teilzeit</option>
                      <option value="Mini-job">Mini-job</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Gehalt (€)"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData?.salary}
                    onChange={(e) => handleInputChange('salary', e?.target?.value === '' ? '' : parseFloat(e?.target?.value) || 0)}
                    error={errors?.salary}
                    disabled={mode === 'view'}
                  />
                  
                  <Input
                    label="Tagesrate (€/Tag)"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData?.tagesrate}
                    onChange={(e) => handleInputChange('tagesrate', e?.target?.value === '' ? '' : parseFloat(e?.target?.value) || 0)}
                    error={errors?.tagesrate}
                    disabled={mode === 'view'}
                  />
                </div>


                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Status
                    </label>
                    <select
                      value={formData?.status}
                      onChange={(e) => handleInputChange('status', e?.target?.value)}
                      disabled={mode === 'view'}
                      className="w-full px-3 py-2 border border-border rounded-md bg-input text-text-primary focus:ring-2 focus:ring-primary focus:border-primary disabled:opacity-50"
                    >
                      <option value="active">Aktiv</option>
                      <option value="inactive">Inaktiv</option>
                      <option value="suspended">Gesperrt</option>
                    </select>
                  </div>
                </div>

                <Input
                  label="Adresse"
                  type="text"
                  value={formData?.address}
                  onChange={(e) => handleInputChange('address', e?.target?.value)}
                  disabled={mode === 'view'}
                />
              </div>
            </div>
          )}

          {activeTab === 'history' && mode === 'view' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-text-primary">Aktuelle Stempelhistorie</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadPunchHistory}
                  loading={punchHistoryLoading}
                  iconName="RefreshCw"
                  iconPosition="left"
                >
                  Aktualisieren
                </Button>
              </div>
              
              {/* Error State */}
              {punchHistoryError && (
                <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="AlertCircle" size={20} className="text-error" />
                    <span className="text-error font-medium">{punchHistoryError}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPunchHistoryError(null)}
                      iconName="X"
                    />
                  </div>
                </div>
              )}
              
              {/* Loading State */}
              {punchHistoryLoading ? (
                <div className="bg-muted/30 rounded-lg p-8 text-center">
                  <Icon name="Loader2" size={32} className="text-primary mx-auto mb-2 animate-spin" />
                  <p className="text-text-secondary">Stempelhistorie wird geladen...</p>
                </div>
              ) : (
                <div className="bg-muted/30 rounded-lg overflow-hidden">
                  {punchHistory?.length > 0 ? (
                    <table className="w-full">
                      <thead className="bg-muted">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">Datum</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">Einchecken</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">Auschecken</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">Stunden</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {punchHistory?.map((record, index) => (
                          <tr key={record.id || index} className="hover:bg-muted/50">
                            <td className="px-4 py-3 text-sm text-text-primary">
                              {record.date}
                            </td>
                            <td className="px-4 py-3 text-sm text-text-primary">{record.clockIn}</td>
                            <td className="px-4 py-3 text-sm text-text-primary">{record.clockOut}</td>
                            <td className="px-4 py-3 text-sm text-text-primary">{record.hours}</td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                record.status === 'completed' 
                                  ? 'bg-success/10 text-success' 
                                  : 'bg-warning/10 text-warning'
                              }`}>
                                {record.status === 'completed' ? 'Abgeschlossen' : 'Unvollständig'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="p-8 text-center">
                      <Icon name="Clock" size={48} className="text-text-secondary mx-auto mb-4" />
                      <h4 className="text-lg font-medium text-text-primary mb-2">Keine Stempelhistorie</h4>
                      <p className="text-text-secondary">
                        {employee?.name} hat sich noch nicht ein- oder ausgecheckt.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            {mode === 'view' ? 'Schließen' : 'Abbrechen'}
          </Button>
          
          {(mode === 'edit' || mode === 'create') && (
            <Button
              variant="default"
              onClick={handleSave}
              loading={isLoading}
              iconName="Save"
              iconPosition="left"
            >
              {mode === 'create' ? 'Mitarbeiter erstellen' : 'Änderungen speichern'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeModal;